import { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getSortedPosts } from '../../data/blogPosts';
import { trackCta } from '../../lib/analytics';

const tagColor: Record<string, { bg: string; fg: string; bd: string }> = {
  Product: { bg: '#e6f0fa', fg: '#0b5394', bd: '#bcd5ee' },
  Coaching: { bg: '#fef7ec', fg: '#92400e', bd: '#fbd599' },
  Guides: { bg: '#e6f4ec', fg: '#0f5132', bd: '#bfe0cd' },
  'Behind the build': { bg: '#eef0f4', fg: '#334155', bd: '#cbd2dc' },
};

function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function BlogIndexPage() {
  const posts = getSortedPosts();

  useEffect(() => {
    document.title = 'Notes — the Baton blog';
    window.scrollTo({ top: 0 });
  }, []);

  const [featured, ...rest] = posts;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 6, md: 9 } }}>
      <Typography
        variant="h3"
        component="h1"
        sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}
      >
        Notes
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1.5, maxWidth: 620 }}>
        Practical posts on running a track and field team in 2026 — what's working,
        what isn't, and what we're shipping next.
      </Typography>

      {featured && (
        <Card
          variant="outlined"
          sx={{
            mt: 5,
            borderColor: 'divider',
            overflow: 'hidden',
            background:
              'linear-gradient(180deg, rgba(220,234,245,0.45) 0%, rgba(255,255,255,1) 60%)',
          }}
        >
          <CardActionArea
            component={RouterLink}
            to={`/blog/${featured.slug}`}
            aria-label={`Read: ${featured.title}`}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Chip
                  size="small"
                  label="Latest"
                  sx={{
                    fontWeight: 700,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                  }}
                />
                <PostMeta post={featured} />
              </Stack>
              <Typography
                variant="h4"
                component="h2"
                sx={{ fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.2 }}
              >
                {featured.title}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1.5, fontSize: 16 }}>
                {featured.excerpt}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 3 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'primary.main' }}
                >
                  Read the post
                </Typography>
                <ArrowForwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
      )}

      {rest.length > 0 && (
        <Stack spacing={2.5} sx={{ mt: 4 }}>
          {rest.map((post) => (
            <Card
              key={post.slug}
              variant="outlined"
              sx={{
                borderColor: 'divider',
                transition: 'border-color 120ms, transform 120ms',
                '&:hover': { borderColor: 'primary.light' },
              }}
            >
              <CardActionArea
                component={RouterLink}
                to={`/blog/${post.slug}`}
                aria-label={`Read: ${post.title}`}
              >
                <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                  <PostMeta post={post} />
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{ mt: 1.5, fontWeight: 700, letterSpacing: '-0.01em' }}
                  >
                    {post.title}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    {post.excerpt}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}

      <Box
        sx={{
          mt: { xs: 6, md: 9 },
          p: { xs: 3, sm: 4 },
          textAlign: 'center',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 3,
          bgcolor: 'background.paper',
        }}
      >
        <Typography sx={{ fontWeight: 600 }}>Want new posts in your inbox?</Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: 14 }}>
          We send a short note when something useful ships. No marketing fluff.
        </Typography>
        <Button
          component={RouterLink}
          to="/#waitlist"
          variant="contained"
          sx={{ mt: 2 }}
          aria-label="Get on the waitlist"
          onClick={() => trackCta('Waitlist', 'blog_index')}
        >
          Get on the waitlist
        </Button>
      </Box>
    </Container>
  );
}

function PostMeta({
  post,
}: {
  post: { tag: string; date: string; readMinutes: number };
}) {
  const colors = tagColor[post.tag] ?? tagColor['Product'];
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ flexWrap: 'wrap', gap: 1 }}
    >
      <Chip
        size="small"
        label={post.tag}
        sx={{
          bgcolor: colors.bg,
          color: colors.fg,
          border: `1px solid ${colors.bd}`,
          fontWeight: 600,
          height: 22,
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {formatDate(post.date)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        ·
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {post.readMinutes} min read
      </Typography>
    </Stack>
  );
}
